import { format, parseISO } from "date-fns"
import type { DcFillRate, InboundAlert } from "@/api/inbound_po_line.tsx"

/** `0.9234` → `92.3%`; nothing to show → an em dash. */
export function format_rate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || Number.isNaN(rate)) {
    return "—"
  }
  return `${(Math.round(rate * 1000) / 10).toFixed(1)}%`
}

/** Chart rows: one per week with anything in its rolling window, rate as a percentage. */
export function chart_rows(dc: DcFillRate): { week: string; label: string; rolling: number; short: number }[] {
  return dc.weeks
    .filter((w) => w.rolling_fill_rate !== null)
    .map((w) => ({
      week: w.week_start,
      label: format(parseISO(w.week_start), "MMM d"),
      rolling: Math.round((w.rolling_fill_rate as number) * 1000) / 10,
      short: w.short,
    }))
}

export type AlertFilter = "unanswered" | "shortages" | "short_dated" | "all"

export function filter_alerts(alerts: InboundAlert[], filter: AlertFilter): InboundAlert[] {
  switch (filter) {
    case "unanswered":
      return alerts.filter((a) => a.is_shortage && a.brand_response === null)
    case "shortages":
      return alerts.filter((a) => a.is_shortage)
    case "short_dated":
      return alerts.filter((a) => a.is_short_dated)
    case "all":
      return alerts
  }
}

/** Sum shortfall dollars in cents, so a long list does not drift. */
export function total_shorted_value(alerts: InboundAlert[]): number {
  const cents = alerts.reduce((sum: number, a: InboundAlert) => sum + Math.round((a.shorted_value ?? 0) * 100), 0)
  return cents / 100
}

/**
 * A pre-filled email to UNFI's inventory desk for a line the brand shipped in full, so the receiving
 * error is on record before it turns into a debit note and a fill-rate fine.
 */
export function receiving_dispute_mailto(alert: InboundAlert, vendor_label: string): string | null {
  if (!alert.inventory_control_email) {
    return null
  }
  const subject = `Receiving discrepancy - PO ${alert.po_number} - ${alert.item_number}`
  const received = alert.received_date ? format(parseISO(alert.received_date), "yyyy-MM-dd") : "the receipt date"
  const body = [
    `Hello,`,
    ``,
    `Your receiving report for PO ${alert.po_number} (received ${received}) shows item ${alert.item_number}${alert.description ? ` (${alert.description})` : ""} received ${alert.quantity_received ?? 0} of ${alert.quantity_ordered ?? 0} cases.`,
    `We shipped this line in full. Please recount or check whether the cases were received under another PO, and correct the receipt. Our bill of lading and proof of delivery are attached.`,
    ``,
    `Thank you,`,
    vendor_label,
  ].join("\n")
  return `mailto:${alert.inventory_control_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function alert(overrides: Partial<InboundAlert>): InboundAlert {
    return {
      id: "1",
      dc: "002",
      dc_label: "East (Concord, ON)",
      po_number: "1250070",
      item_number: "AYM300",
      brand_sku: "17258",
      description: "TURMERIC LATTE",
      received_date: "2026-01-08",
      quantity_ordered: 176,
      quantity_received: 26,
      quantity_short: 150,
      shorted_value: 7423.5,
      is_shortage: true,
      is_short_dated: false,
      short_dated_cases: null,
      expiry_date: null,
      days_short_of_guarantee: null,
      inventory_control_email: "GCInvControl@unfi.com",
      brand_response: null,
      brand_response_note: null,
      brand_response_at: null,
      ...overrides,
    }
  }

  describe("format_rate", () => {
    it("rounds to one decimal and handles missing", () => {
      expect(format_rate(0.98765)).toBe("98.8%")
      expect(format_rate(1)).toBe("100.0%")
      expect(format_rate(null)).toBe("—")
    })
  })

  describe("filter_alerts", () => {
    it("unanswered means an unanswered shortage", () => {
      const rows = [
        alert({ id: "a" }),
        alert({ id: "b", brand_response: "confirmed_short" }),
        alert({ id: "c", is_shortage: false, is_short_dated: true }),
      ]
      expect(filter_alerts(rows, "unanswered").map((a) => a.id)).toEqual(["a"])
      expect(filter_alerts(rows, "short_dated").map((a) => a.id)).toEqual(["c"])
      expect(filter_alerts(rows, "all")).toHaveLength(3)
    })
  })

  describe("total_shorted_value", () => {
    it("sums in cents", () => {
      expect(total_shorted_value([alert({ shorted_value: 0.1 }), alert({ shorted_value: 0.2 }), alert({ shorted_value: null })])).toBe(0.3)
    })
  })

  describe("chart_rows", () => {
    it("skips weeks with an empty window and converts to percent", () => {
      const rows = chart_rows({
        dc: "002",
        label: "East",
        current_rolling_fill_rate: 0.8,
        below_threshold: true,
        short_cases_in_window: 20,
        shorted_value_in_window: 0,
        weeks: [
          { week_start: "2026-01-05", ordered: 0, received: 0, short: 0, shorted_value: 0, rolling_ordered: 0, rolling_received: 0, rolling_fill_rate: null },
          { week_start: "2026-01-12", ordered: 100, received: 80, short: 20, shorted_value: 0, rolling_ordered: 100, rolling_received: 80, rolling_fill_rate: 0.8 },
        ],
      })
      expect(rows).toEqual([{ week: "2026-01-12", label: "Jan 12", rolling: 80, short: 20 }])
    })
  })

  describe("receiving_dispute_mailto", () => {
    it("addresses UNFI's inventory desk with the PO and counts", () => {
      const link = receiving_dispute_mailto(alert({}), "Cha's Organics")!
      expect(link.startsWith("mailto:GCInvControl@unfi.com?subject=")).toBe(true)
      expect(decodeURIComponent(link)).toContain("received 26 of 176 cases")
      expect(receiving_dispute_mailto(alert({ inventory_control_email: null }), "x")).toBeNull()
    })
  })
}
